/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { BuildOutlined, DownOutlined } from '@ant-design/icons';
import { Dropdown, Form, MenuProps } from 'antd';
import dayjs from 'dayjs';

import { Node } from '@/client';
import { GuardedLink } from '@/components/GuardedLink';
import { IncomingData } from '@/components/Node/IncomingData';
import { NodeViewPageCard } from '@/components/Node/NodeViewPageCard';
import { RelativeTime } from '@/components/RelativeTime';
import { useAuthorization } from '@/hooks/useAuthorization';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Col } from '@/primitives/Col';
import { Flex } from '@/primitives/Flex';
import { WarningIcon } from '@/primitives/Icons';
import { Row } from '@/primitives/Row';
import { Text } from '@/primitives/Text';

import { Status } from './Status';

type NodeConfigDropdownProps = {
  assignNewNodeConfig: (node: Node) => void;
  assignNewConfigRevision: (node: Node) => void;
  node: Node;
};

function NodeConfigDropdown({ assignNewNodeConfig, assignNewConfigRevision, node }: NodeConfigDropdownProps) {
  const { t } = useMimicTranslation('node');
  const isAdmin = useAuthorization('admin');

  if (!isAdmin || node.dateArchived) return null;

  const items: MenuProps['items'] = [
    {
      key: 'selectConfig',
      label: (
        <div style={{ padding: '4px 0' }}>
          <Text>{t('selectNewConfig')}</Text>
        </div>
      ),
      onClick: () => assignNewNodeConfig(node),
      disabled: node.hasRunningJob,
    },
    {
      key: 'selectRevision',
      label: (
        <div style={{ padding: '4px 0' }}>
          <Text>{t('selectNewRevision')}</Text>
        </div>
      ),
      onClick: () => assignNewConfigRevision(node),
      disabled: node.hasRunningJob,
    },
  ];

  return (
    <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
      <span>
        <Button type="default" style={{ display: 'flex', alignItems: 'center' }}>
          <Text type="default" size="sm" strong style={{ marginRight: '4px' }}>
            {t('configureNode')}
          </Text>
          <DownOutlined style={{ fontSize: '10px' }} />
        </Button>
      </span>
    </Dropdown>
  );
}

export type NodeConfigDetailProps = {
  node: Node;
  title: string;
  lastUpdatedByName: string | undefined;
  lastUpdateByDate: string | undefined;
  nodeConfigName: string | undefined;
  nodeConfigId: string | undefined;
  revisionNumber: number | undefined;
  revisionDescription: string | undefined;
  revisionId: string | undefined;
  assignNewNodeConfig: (node: Node) => void;
  assignNewConfigRevision: (node: Node) => void;
  getNodeConfigPath: (node: Node) => string;
  getNodeConfigRevisionPath: (node: Node) => string;
};

export function NodeConfigDetails({
  node,
  title,
  lastUpdatedByName,
  lastUpdateByDate,
  nodeConfigName,
  nodeConfigId,
  revisionNumber,
  revisionDescription,
  revisionId,
  assignNewNodeConfig,
  assignNewConfigRevision,
  getNodeConfigPath,
  getNodeConfigRevisionPath,
}: NodeConfigDetailProps) {
  const { t } = useMimicTranslation('node');

  const isPending = node.requestedRevisionState.status === 'pending';

  return (
    <NodeViewPageCard bodyStyles={{ paddingBottom: '0px', paddingTop: '16px' }}>
      <Flex vertical gap="xxs">
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <Text type="default" style={{ fontWeight: 600, fontSize: '1.125rem' }}>
              {title}
            </Text>
            {node.appliedRevisionState?.nodeConfig?.dateArchived && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>-</span>
                <WarningIcon />
                <Text type="subtle" size="md">
                  {t('archivedConfigurationWarning')}
                </Text>
              </div>
            )}
          </div>
          {title === t('currentConfiguration') ? (
            <NodeConfigDropdown
              assignNewNodeConfig={assignNewNodeConfig}
              assignNewConfigRevision={assignNewConfigRevision}
              node={node}
            />
          ) : null}
        </div>
        <Text type="subtle" size="md">
          {t('lastUpdatedBy')} {lastUpdatedByName},{' '}
          {lastUpdateByDate && dayjs(lastUpdateByDate).utc().format('D MMM YYYY HH:mm:ss')} UTC (
          {lastUpdateByDate && <RelativeTime date={lastUpdateByDate} />})
        </Text>
      </Flex>
      <Status node={node} getNodeConfigPath={getNodeConfigPath} getNodeConfigRevisionPath={getNodeConfigRevisionPath} />
      <div style={{ padding: '16px 0' }}>
        <Flex vertical>
          <Form layout="vertical">
            <Flex vertical>
              <IncomingData label={t('configuration')} loading={isPending} marginBottom={0}>
                <Row gutter={[16, 16]} align="middle" justify="space-between">
                  <Col span={24}>
                    <GuardedLink to={getNodeConfigPath(node)} dataTestId="applied-configuration" requiredRole="admin">
                      <BuildOutlined style={{ marginRight: '4px' }} />
                      {nodeConfigName}
                    </GuardedLink>
                  </Col>
                  <Col xs={24} lg={16} xl={14} xxl={16}>
                    <Text type="subtle" mono copyable>
                      {nodeConfigId}
                    </Text>
                  </Col>
                </Row>
              </IncomingData>
              <IncomingData label={t('revision')} loading={isPending} marginBottom={0}>
                <Row gutter={[16, 16]} align="middle" justify="space-between">
                  <Col span={24}>
                    <GuardedLink
                      to={getNodeConfigRevisionPath(node)}
                      dataTestId="applied-revision"
                      requiredRole="admin"
                    >
                      {`#${revisionNumber} · ${revisionDescription}`}
                    </GuardedLink>
                  </Col>
                  <Col xs={24} lg={16} xl={14} xxl={16}>
                    <Text type="subtle" mono copyable>
                      {revisionId}
                    </Text>
                  </Col>
                </Row>
              </IncomingData>
            </Flex>
          </Form>
        </Flex>
      </div>
    </NodeViewPageCard>
  );
}
